// Billing & Plans view: populate the plan summary, invoice history, and Lemon customer-portal links from
// /api/billing-summary. The plan cards + Buy/Subscribe buttons are static HTML wired in main.ts (they
// mint a checkout); this module fills the dynamic parts each time the view opens. Degrades safely: on any
// error the page still shows the cards, an empty history, and the policy links.

import { getBillingSummary } from '../lib/api.js';

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}
function show(e: HTMLElement | null, on: boolean): void {
  if (e) e.hidden = !on;
}
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso || '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
function esc(s: string): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

export async function renderBilling(): Promise<void> {
  const planName = el('billPlanName');
  const callsLeft = el('billCallsLeft');
  const nextRenewal = el('billNextRenewal');
  const manage = el('billManage') as HTMLAnchorElement | null;
  const cancel = el('billCancel') as HTMLAnchorElement | null;
  const portalBtn = el('billPortalBtn') as HTMLAnchorElement | null;
  const body = el('billHistoryBody');
  const empty = el('billHistoryEmpty');

  try {
    const s = await getBillingSummary();
    // Plan name comes from an active Lemon subscription; pack-only / not-yet-subscribed accounts have none,
    // so show "Prepaid" (the Call-packs model), not a retired "pay-as-you-go" tier.
    if (planName) planName.textContent = s.plan ?? 'Prepaid';
    // Clamp to 0 for display: the raw balance can go negative while enforcement is record-only, which would
    // read as broken to a customer. "0 calls left" is the honest, actionable message (buy more).
    if (callsLeft) callsLeft.textContent = String(Math.max(0, s.callsLeft ?? 0));
    if (nextRenewal) nextRenewal.textContent = s.nextRenewal ? fmtDate(s.nextRenewal) : s.plan ? '—' : 'No subscription';

    // Manage/Cancel/Portal are real only with a Lemon portal URL. Without one (pack-only or not-yet-
    // subscribed, like a fresh account) there is nothing to manage or cancel, so hide all three — the
    // footer policy links still explain how to cancel or request a refund.
    if (s.portalUrl) {
      if (manage) manage.href = s.portalUrl;
      show(manage, true);
      if (cancel) cancel.href = s.portalUrl;
      show(cancel, true);
      if (portalBtn) portalBtn.href = s.portalUrl;
      show(portalBtn, true);
    } else {
      show(manage, false);
      show(cancel, false);
      show(portalBtn, false);
    }

    // Invoice rows.
    if (body) {
      if (s.invoices.length) {
        body.innerHTML = s.invoices
          .map((inv) => {
            const dl = inv.url
              ? `<a class="b2-dl" href="${esc(inv.url)}" target="_blank" rel="noopener">Download &#8595;</a>`
              : '<span class="b2-mut">—</span>';
            return `<tr><td>${esc(fmtDate(inv.date))}</td><td>${esc(inv.item)}</td><td class="b2-amt">${esc(inv.amount)}</td><td>${dl}</td></tr>`;
          })
          .join('');
        show(empty, false);
      } else {
        body.innerHTML = '';
        show(empty, true);
      }
    }
  } catch {
    if (planName) planName.textContent = '—';
    if (callsLeft) callsLeft.textContent = '—';
    if (nextRenewal) nextRenewal.textContent = '—';
    show(manage, false);
    show(cancel, false);
    show(portalBtn, false);
    if (body) body.innerHTML = '';
    show(empty, true);
  }
}
